import { useMemo, useState } from 'react'
import GpsFixedRoundedIcon from '@mui/icons-material/GpsFixedRounded'
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval'
import { useTimeline } from '../../context/useTimeline'
import type { TimelineElement } from '../../context/TimelineContext'
import { CanvasOverlay } from '../Canvas/CanvasOverlay'

/**
 * Perimeter Countdown View — concentric "radar" rings keyed to time-to-deadline.
 *
 * Tasks + subtasks with a valid `endDate` are bucketed onto 3 rings by days
 * remaining (deadline interpreted as end-of-day local), then distributed
 * evenly around the circumference of their ring so nothing overlaps. A single
 * 1-Hz ticker re-renders the HH:MM:SS chips.
 *
 * Not wired into DashboardContainer's switcher — standalone file per
 * track-isolation rule.
 */

const DRAGGABLE_TYPES: ReadonlyArray<TimelineElement['type']> = ['TASK', 'SUBTASK']

const RING_RADIUS = {
  CRITICAL: 100,
  WARNING: 220,
  OUTER: 360,
} as const

const NODE_SIZE = 48 // 24px half — matches the spec's `- 24px` offset.
const NODE_HALF = NODE_SIZE / 2

type RingBucket = 'CRITICAL' | 'WARNING' | 'OUTER'

interface PlottedItem {
  element: TimelineElement
  deadlineMs: number
  daysRemaining: number
  bucket: RingBucket
  isOverdue: boolean
  isDone: boolean
}

/** Parse `YYYY-MM-DD` to local end-of-day. Deadlines mean "by close of day". */
const parseDeadline = (raw: string | null | undefined): number | null => {
  if (!raw) return null
  const head = raw.length >= 10 ? raw.slice(0, 10) : raw
  if (!/^\d{4}-\d{2}-\d{2}$/.test(head)) return null
  const [y, m, d] = head.split('-').map(Number)
  // Local end-of-day. Don't use `new Date(raw)` — it parses YYYY-MM-DD as UTC
  // midnight and would drift the deadline by the TZ offset.
  return new Date(y, m - 1, d, 23, 59, 59, 999).getTime()
}

const MS_PER_DAY = 24 * 60 * 60 * 1000

const bucketize = (daysRemaining: number, isOverdue: boolean): RingBucket => {
  if (isOverdue || daysRemaining <= 2) return 'CRITICAL'
  if (daysRemaining <= 7) return 'WARNING'
  return 'OUTER'
}

const RING_COLOR: Record<RingBucket, { ring: string; text: string; border: string }> = {
  CRITICAL: {
    ring: 'stroke-[var(--danger)]/40',
    text: 'text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]',
    border: 'border-[var(--danger)]',
  },
  WARNING: {
    ring: 'stroke-[var(--warning)]/40',
    text: 'text-[color-mix(in_srgb,var(--warning)_60%,var(--text-strong))]',
    border: 'border-[var(--warning)]',
  },
  OUTER: {
    ring: 'stroke-[var(--muted)]/40',
    text: 'text-[var(--muted)]',
    border: 'border-[var(--border)]',
  },
}

/** Pad and format ms remaining into `HH:MM:SS` (clamped at 0). */
const formatCountdown = (msRemaining: number): string => {
  const clamped = Math.max(0, msRemaining)
  const totalSeconds = Math.floor(clamped / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`
}

/** Format elapsed-since-deadline as a `+HH:MM:SS` overdue stamp. */
const formatOverdue = (msSinceDeadline: number): string =>
  `+${formatCountdown(msSinceDeadline)}`

/** Compact 4-char identifier badge — Firestore IDs are too long for a chip. */
const shortIdOf = (id: string): string => id.slice(0, 4).toUpperCase()

export const PerimeterCountdownView = () => {
  const { elements } = useTimeline()

  // Single 1-Hz ticker drives every countdown chip below. One interval, not N.
  const [nowMs, setNowMs] = useState(() => Date.now())
  useVisibilityAwareInterval(() => setNowMs(Date.now()), 1000)

  // Memoize on element identity + endDate signature, not nowMs — the bucket
  // assignment shifts at most once per day per item, so recomputing once per
  // second would be wasteful.
  const plotted = useMemo<PlottedItem[]>(() => {
    const out: PlottedItem[] = []
    for (const el of elements) {
      if (!DRAGGABLE_TYPES.includes(el.type)) continue
      const deadlineMs = parseDeadline(el.endDate)
      if (deadlineMs === null) continue
      const msRemaining = deadlineMs - nowMs
      const isOverdue = msRemaining < 0
      // Days-remaining is computed off the current snapshot of nowMs, but we
      // only use it for bucket assignment (re-derived in the ticker effect
      // when the boundary actually crosses).
      const daysRemaining = Math.max(0, Math.ceil(msRemaining / MS_PER_DAY))
      const isDone = (el.progress ?? 0) >= 100
      out.push({
        element: el,
        deadlineMs,
        daysRemaining,
        bucket: bucketize(daysRemaining, isOverdue),
        isOverdue,
        isDone,
      })
    }
    // Chronological order — earliest deadline first within each ring.
    out.sort((a, b) => a.deadlineMs - b.deadlineMs)
    return out
    // Intentionally NOT depending on nowMs — bucket re-evaluation per
    // second would re-arrange the radar visually every tick. The ticker
    // chips still re-render via the closure over nowMs below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elements])

  // Group by ring for angular distribution.
  const byBucket = useMemo(() => {
    const map: Record<RingBucket, PlottedItem[]> = { CRITICAL: [], WARNING: [], OUTER: [] }
    for (const item of plotted) map[item.bucket].push(item)
    return map
  }, [plotted])

  const totalCount = plotted.length
  const overdueCount = plotted.filter((p) => p.isOverdue && !p.isDone).length

  return (
    <div className="relative w-full h-full bg-[var(--bg)] overflow-hidden text-[var(--text)]">
      {/* ── Status header ─────────────────────────────────────────────── */}
      <CanvasOverlay anchor="viewport">
        <div className="absolute top-6 left-1/2 -translate-x-1/2 z-30 bg-[var(--surface-card)]/80 backdrop-blur text-xs font-semibold px-4 py-2 rounded-full text-[var(--muted)] border border-[var(--border)] pointer-events-none">
          <GpsFixedRoundedIcon fontSize="inherit" /> Perimeter Active — Tracking <span className="text-[var(--text-strong)]">{totalCount}</span> deadlines
          {overdueCount > 0 && (
            <> · <span className="text-[color-mix(in_srgb,var(--danger)_60%,var(--text-strong))]">{overdueCount} overdue</span></>
          )}
        </div>
      </CanvasOverlay>

      {/* ── Concentric ring sectors (SVG, decorative) ─────────────────── */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        aria-hidden="true"
      >
        <g style={{ transform: 'translate(50%, 50%)' }}>
          {(Object.keys(RING_RADIUS) as RingBucket[]).map((bucket) => (
            <circle
              key={bucket}
              cx={0}
              cy={0}
              r={RING_RADIUS[bucket]}
              fill="none"
              className={`${RING_COLOR[bucket].ring} stroke-1`}
              strokeDasharray="4 4"
            />
          ))}
          {/* Center bullseye dot */}
          <circle cx={0} cy={0} r={4} style={{ fill: 'var(--danger)' }} />
        </g>
      </svg>

      {/* ── Ring labels (top-aligned to each ring) ────────────────────── */}
      <CanvasOverlay anchor="viewport">
        <div className="absolute inset-0 pointer-events-none">
          {(Object.keys(RING_RADIUS) as RingBucket[]).map((bucket) => (
            <span
              key={bucket}
              className={`absolute left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.18em] font-semibold ${RING_COLOR[bucket].text}`}
              style={{ top: `calc(50% - ${RING_RADIUS[bucket] + 16}px)` }}
            >
              {bucket === 'CRITICAL' ? '0-2d' : bucket === 'WARNING' ? '3-7d' : '8-14d+'}
            </span>
          ))}
        </div>
      </CanvasOverlay>

      {/* ── Plotted item chips ────────────────────────────────────────── */}
      {(Object.keys(byBucket) as RingBucket[]).map((bucket) => {
        const items = byBucket[bucket]
        const radius = RING_RADIUS[bucket]
        const colors = RING_COLOR[bucket]
        return items.map((item, index) => {
          const angle = (index / Math.max(items.length, 1)) * 2 * Math.PI - Math.PI / 2
          const left = `calc(50% + ${radius * Math.cos(angle)}px - ${NODE_HALF}px)`
          const top = `calc(50% + ${radius * Math.sin(angle)}px - ${NODE_HALF}px)`
          const msRemaining = item.deadlineMs - nowMs
          const tickerLabel = item.isDone
            ? 'DONE'
            : item.isOverdue
            ? formatOverdue(-msRemaining)
            : formatCountdown(msRemaining)
          // Done = green safe-asset accent overrides ring tier color.
          const doneAccent = 'border-[var(--success)] text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]'
          const cardClass = item.isDone ? doneAccent : `${colors.border} ${colors.text}`
          const shouldPulse = bucket === 'CRITICAL' && !item.isDone
          return (
            <div
              key={item.element.id}
              className="absolute z-10"
              style={{ left, top, width: NODE_SIZE * 3.5 }}
              title={`${item.element.title} · due ${item.element.endDate ?? ''}`}
            >
              <div
                className={`bg-[var(--surface-card)]/95 backdrop-blur rounded-lg border px-2.5 py-1.5 shadow-lg flex flex-col items-center gap-0.5 ${cardClass} ${shouldPulse ? 'animate-pulse' : ''}`}
              >
                <div className="flex items-center gap-1.5 max-w-full">
                  <span className="text-[8px] font-mono px-1 py-0.5 rounded bg-[var(--surface-muted)] text-[var(--muted)] tracking-wider">
                    {shortIdOf(item.element.id)}
                  </span>
                  <span className="text-[11px] font-semibold text-[var(--text-strong)] truncate">
                    {item.element.title}
                  </span>
                </div>
                <span className={`text-[10px] font-mono tabular-nums tracking-wider ${item.isDone ? 'text-[color-mix(in_srgb,var(--success)_60%,var(--text-strong))]' : colors.text}`}>
                  {tickerLabel}
                </span>
              </div>
            </div>
          )
        })
      })}

      {/* ── Empty state ──────────────────────────────────────────────── */}
      {totalCount === 0 && (
        <CanvasOverlay anchor="viewport">
          <div className="absolute inset-0 flex items-center justify-center text-[var(--muted)] text-sm">
            No deadlines on the perimeter. Set an end date on a task to track it here.
          </div>
        </CanvasOverlay>
      )}
    </div>
  )
}
